import {Button, Card, Col, Input, List, Modal, Row, Select, Table, Tooltip} from "antd";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  addNewRup,
  addNewRupState, createNewRupFromRecommendedAction,
  duplicateRup, editRups,
  fetchRecommendedRups,
  fetchRups,
  getRupHelpers,
  resetNewRupState
} from "../../store/pages/rup/actions";
import {selectStore} from "../../store/selector";
import {CopyOutlined, EditOutlined, EyeOutlined, UndoOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {generatePath, useNavigate} from "react-router-dom";
import {ROUTE_PATH, RUP_BASE_TYPES} from "../../constants";
import {InnerTabs, MyButton, MyText, OuterTabs} from "../../components";

export const Rup = () => {
  const dispatch = useDispatch()
  const {t} = useTranslation('rup')
  const navigate = useNavigate();

  const [visibleModal, setVisibleModal] = useState(false)
  const [currentTab, setCurrentTab] = useState<string>('1')

  const yearsArray = [2020, 2021, 2022, 2023, 2024]

  const {
    rup: {
      rups: rupsState,
      currentRup: currentRupState,
      rupsHelpers: rupsHelpersState,
      recommendedRups: recommendedRupsState
    }
  } = useSelector(selectStore)

  const RecommendedRupsComponent = useMemo(() => {
    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <MyText size={20} weight={700}>
            {t('featuredSpecialty')}
          </MyText>
        </Col>
        <Col span={24}>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 4,
              lg: 4,
              xl: 4,
              xxl: 4,
            }}
            pagination={{pageSize: 9}}
            loading={recommendedRupsState.loading}
            dataSource={recommendedRupsState.data}
            renderItem={(item: any, index: any) => (
              <List.Item>
                <Card>
                  <Row gutter={[16, 8]}>
                    {
                      item.profile &&
                      (
                        <Col span={24}>
                          <MyText>
                            {t('table.profile')}:
                          </MyText>
                          <MyText>
                            {item.profile}
                          </MyText>
                        </Col>
                      )
                    }
                    {
                      item.profile_direction &&
                      (
                        <Col span={24}>
                          <MyText>
                            {t('table.profileRoute')}:
                          </MyText>
                          <MyText>
                            {item.profile_direction}
                          </MyText>
                        </Col>
                      )
                    }
                    {
                      item.specialization && (
                        <Col span={24}>
                          <MyText>
                            {t('table.specialization')}:
                          </MyText>
                          <MyText>
                            {item.specialization}
                          </MyText>
                        </Col>
                      )
                    }
                    {
                      item.speciality && (
                        <Col span={24}>
                          <MyText>
                            {t('table.specialty')}:
                          </MyText>
                          <MyText>
                            {item.speciality}
                          </MyText>
                        </Col>
                      )
                    }
                    {
                      item.group && (
                        <Col span={24}>
                          <MyText>
                            {t('table.forGroup')}:
                          </MyText>
                          <MyText>
                            {item.group}
                          </MyText>
                        </Col>
                      )
                    }
                    {
                      RUP_BASE_TYPES().find(rupType => rupType.id === item.base_type) &&
                        <Col span={24}>
                            <MyText>
                              {t('table.onBase')}:
                            </MyText>
                            <MyText>
                              {RUP_BASE_TYPES().find(rupType => rupType.id === item.base_type)?.caption}
                            </MyText>
                        </Col>
                    }

                    <Col span={24}>
                      <MyButton
                        onClick={() => handleNewRupFromRecommended(item, index)}
                        title={t('button.createRup')}/>
                    </Col>
                  </Row>
                </Card>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    )
  }, [recommendedRupsState, RUP_BASE_TYPES])

  const columns = [
    {
      title: `${t('table.index')}`,
      dataIndex: 'id',
      width: 40,
      render: (text: any, record: any, index: any) => (
        <span key={record.id}>{text}</span>
      )
    },
    {
      title: `${t('table.action')}`,
      width: 120,
      render: (text: any, record: any, index: any) => (
        <Row justify='space-around' key={record.id}>
          <Col>
            <Button type='text' icon={<EyeOutlined/>} onClick={() => handleViewRup(record)}
                    disabled={!record.speciality}/>
          </Col>
          <Col>
            <Button type='text' icon={<EditOutlined/>} style={{top: 0}}
                    onClick={() => handleEditRup(record)}/>
          </Col>
          <Col>
            <Button type='text' icon={<CopyOutlined style={{color: '#1c96ed'}}/>} style={{top: 0}}
                    onClick={() => handleDuplicateRup(record)}/>
          </Col>
        </Row>
      )
    },
    {
      title: `${t('table.rupTitle')}`,
      dataIndex: 'name',
      render: (text: any, record: any, index: any) => (
        <p key={record.id}>{(!text || text === 'null') ? '' : text}</p>
      )
    },
    {
      title: `${t('table.specialty')}`,
      dataIndex: 'speciality_id',
      render: (text: any, record: any, index: any) => (
        <p key={record.id}>{record?.speciality?.caption}</p>
      )
    },
    {
      title: `${t('groups')}`,
      dataIndex: 'type',
      render: (text: any, record: any, index: any) => (
        record?.linkGroups?.length > 2 ?
          <Tooltip
            title={record?.linkGroups?.map((item: any) => <p key={item.id}>{item?.group?.caption}</p>)}>
            {record?.linkGroups?.filter((item: any, index: any) => index < 2).map((item: any) => (
              <p key={item.id} style={{cursor: 'pointer'}}>{item?.group?.caption}</p>))}...
          </Tooltip>
          :
          record?.linkGroups?.map((item: any) => (<p key={item.id}>{item?.group?.caption}</p>))
      )
    },
    {
      title: `${t('table.classBases')}`,
      dataIndex: 'type',
      render: (text: any, record: any, index: any) => (
        <div key={record.id}>
          {RUP_BASE_TYPES().find((rupType: any) => rupType.id === record.type)?.caption}
        </div>
      )
    },
  ]

  const columnsDeleted = [
    {
      title: `${t('table.index')}`,
      dataIndex: 'id',
    },
    {
      title: `${t('table.rupName')}`,
      width: '325px',
      dataIndex: 'name',
    },
    {
      key: 'profile_id',
      title: `${t('table.profile')}`,
      dataIndex: 'profile_id',
      render: (text: any, record: any, index: any) => (
        <p key={record.id}>{record.profile ? record.profile.caption + " - " + record.profile.code : null}</p>
      )
    },
    {
      title: `${t('table.profileRoute')}`,
      dataIndex: 'profile_direction_id',
      render: (text: any, record: any, index: any) => (
        <p
          key={record.id}>{record.profile_direction ? record.profile_direction.caption + " - " + record.profile_direction.code : null}</p>
      )
    },
    {
      title: `${t('table.specialization')}`,
      dataIndex: 'specialization_id',
      render: (text: any, record: any, index: any) => (
        <p
          key={record.id}>{record.specialization ? record.specialization.caption + " - " + record.specialization.code : null}</p>
      )
    },
    {
      title: `${t('table.specialty')}`,
      dataIndex: 'speciality_id',
      render: (text: any, record: any, index: any) => (
        <p key={record.id}>{record.speciality ? record.speciality.caption + " - " + record.speciality.code : null}</p>
      )
    },
    {
      title: `${t('table.classBases')}`,
      dataIndex: 'type',
      render: (text: any, record: any, index: any) => (
        <>
          {RUP_BASE_TYPES().find((rupType: any) => rupType.id === record.type)?.caption}
        </>
      )
    },
    {
      title: `${t('groups')}`,
      dataIndex: 'type',
      render: (text: any, record: any, index: any) => (
        record?.linkGroups?.length > 2 ?
          <Tooltip
            title={record?.linkGroups?.map((item: any) => <p key={item.id}>{item?.group?.caption}</p>)}>
            {record?.linkGroups?.filter((item: any, index: any) => index < 2).map((item: any) => (
              <p key={item.id} style={{cursor: 'pointer'}}>{item.group && item.group.caption}</p>))}...
          </Tooltip>
          :
          record?.linkGroups?.map((item: any) => (<p key={item.id}>{item?.group?.caption}</p>))
      )
    },
    {
      title: `${t('table.action')}`,
      render: (text: any, record: any, index: any) => (
        <Row justify='center'>
          <Col>
            <Button key={record.id} icon={<UndoOutlined/>}
                    onClick={() => handleRevertRup(record, index)}/>
          </Col>
        </Row>

      )
    }
  ]

  useEffect(() => {
    // @ts-ignore
    dispatch(fetchRups())
    // @ts-ignore
    dispatch(fetchRecommendedRups())
    dispatch(getRupHelpers({key: 'profile', value: undefined}))
    dispatch(getRupHelpers({key: 'speciality', value: undefined}))
  }, [])

  const handleEditRup = useCallback((record: any) => {
    setVisibleModal(true)

    dispatch(getRupHelpers({key: 'linkGroups', value: record}))

    dispatch(addNewRupState({...record, modalTitle: `${t('editRup')}`, openType: 'edit'}))
  }, [])

  const handleViewRup = useCallback((record: any) => {
    const path = {
      pathname: generatePath(ROUTE_PATH.RUP_QUALIFICATION, {rupId: record.id}),
    }

    navigate(path)
  }, [])

  const handleDuplicateRup = useCallback((record: any) => {
    let selectedYear: any
    Modal.confirm({
      title: (
        <Row>
          <Col span={24}>
            Год
          </Col>

          <Col span={24}>
            <Select style={{width: '100%'}} options={yearsArray.map(item => ({
              label: item,
              value: item
            }))} onSelect={(value, option) => selectedYear = value}/>
          </Col>
        </Row>
      ),
      okText: `${t('button.save')}`,
      cancelText: `${t('reject')}`,
      okType: 'primary',
      centered: true,
      onOk: () => {
        dispatch(duplicateRup({
          rup_id: record.id,
          year: selectedYear
        }))
      },
    })
  }, [])

  const handleRevertRup = useCallback((record: any, index: any) => {
    let body = {
      ...record,
      status: 1,
      editType: 'revert',
      year: record.year
    }
    dispatch(editRups(body))
  }, [])

  const handleCreateRup = useCallback(() => {
    setVisibleModal(true)

    let bodyNewRup = {
      modalTitle: `${t('addRup')}`,
      status: 1,
      openType: 'create'
    }
    dispatch(addNewRupState(bodyNewRup))
  }, [])

  const handleTabChange = useCallback((key: any) => {
    setCurrentTab(key)
  }, [])

  const handleCloseModal = useCallback(() => {
    // @ts-ignore
    dispatch(resetNewRupState())
    setVisibleModal(false)
  }, [])

  const handleOkModal = useCallback(() => {
    let body = {
      id: currentRupState?.data?.id,
      rup_type: currentRupState?.data?.rup_type,
      speciality_id: currentRupState?.data?.speciality.id,
      name: currentRupState?.data?.name,
      year: currentRupState?.data?.year,
      ...(currentRupState?.data?.linkGroups ? {linkGroups: currentRupState?.data?.linkGroups?.map((item: any) => item.value)} : {linkGroups: []}),
      status: currentRupState?.data?.status,
    }

    if (currentRupState?.data?.openType === 'edit') {
      dispatch(editRups(body))
    } else if (currentRupState?.data?.openType === 'create') {
      dispatch(addNewRup(body))
    }

    // @ts-ignore
    dispatch(resetNewRupState())
    setVisibleModal(false)
  }, [currentRupState])

  const handleChangeModalInput = useCallback((value: any, key: any) => {
    let body = {
      [key]: value,
    }
    dispatch(addNewRupState(body))

    if (currentRupState?.data?.year || currentRupState?.data?.rup_type) {
      switch (key) {
        case 'rup_type':
          let typeBodyForGroup = {
            rup_type: value,
            year: currentRupState?.data?.year,
            ...(currentRupState?.data?.openType === 'create' ? {} : {rup_id: currentRupState?.data?.id})
          }

          dispatch(getRupHelpers({key: 'linkGroups', value: typeBodyForGroup}))
          break
        case 'year':
          let typeYearForGroup = {
            rup_type: currentRupState?.data?.rup_type,
            year: value,
            ...(currentRupState?.data?.openType === 'create' ? {} : {rup_id: currentRupState?.data?.id})
          }

          dispatch(getRupHelpers({key: 'linkGroups', value: typeYearForGroup}))
          break
        default:
          return false
      }
    }

  }, [currentRupState])

  const handleNewRupFromRecommended = useCallback((item: any, index: any) => {
    let body = {
      index,
      profile: item.profile_id,
      profile_direction: item.profile_direction_id,
      specialization: item.specialization_id,
      speciality: item.speciality_id,
      rup_type: item.rup_type,
    }
    dispatch(createNewRupFromRecommendedAction(body))
  }, [])

  const outerTabs = useMemo(() => {
    return rupsState.data?.map((outer: any) => {
      let currentColumn = outer?.status === 1 ? columns : columnsDeleted
      let currentTableScroll = outer?.status === 1 ? true : 1600

      let items = [...Array.from(new Set(outer?.data?.map((inner: any) => inner?.year)))]?.sort((a: any, b: any) => a > b ? 1 : -1).map((year: any) => ({
        key: String(year),
        label: year,
        children: (
          <Table pagination={false}
                 columns={currentColumn}
                 dataSource={outer.data.filter((current: any) => current?.year === year)}
            // @ts-ignore
                 scroll={{x: currentTableScroll}}
                 rowKey={record => record.id}/>
        ),
      }))

      return {
        key: String(outer.key),
        label: outer.label,
        children: <InnerTabs items={items} defaultActiveKey={String(items[0]?.key)}/>,
      }
    })
  }, [rupsState])

  return (
    <>
      <Row gutter={[16, 16]} justify='center' style={{marginLeft: 0, marginRight: 0}}>
        <Col span={24}>
          <OuterTabs items={outerTabs} onTabClick={(key: any) => handleTabChange(key)} activeKey={currentTab}
                     tabBarExtraContent={currentTab === '1' && (
                       <MyButton
                         onClick={handleCreateRup}
                         disabled={rupsState.loading}
                       >{t('button.createRup')}</MyButton>
                     )}/>
        </Col>
        {currentTab === '1' && <Col span={24}>{RecommendedRupsComponent}</Col>}
      </Row>

      <Modal title={currentRupState?.data?.modalTitle} open={visibleModal} onCancel={handleCloseModal}
             onOk={handleOkModal} cancelText={'Закрыть'} okText={'Сохранить'} className='modal-restore'>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <MyText>
              {t('rupName')}
            </MyText>

            <Input value={currentRupState?.data?.name}
                   onChange={(e: any) => handleChangeModalInput(e.target.value, 'name')}/>
          </Col>

          <Col span={12}>
            <MyText>
              {t('table.classBases')}
            </MyText>

            <Select style={{width: '100%'}}
                    value={currentRupState?.data?.rup_type}
                    onChange={(value: any, option: any) => handleChangeModalInput(value, 'rup_type')}
                    showSearch
                    filterOption={(input: any, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={currentRupState?.data?.openType === 'edit'}
            >
              {RUP_BASE_TYPES().map((item, index) => (
                <Select.Option key={index + item.id + item.caption}
                               id={item.id}
                               value={item.id}
                               item={{
                                 id: item.id,
                                 caption: item.caption,
                               }}>
                  {item.caption}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <MyText>
              {t('select.year.label')}
            </MyText>

            <Select style={{width: '100%'}}
                    value={currentRupState?.data?.year}
                    onChange={(value: any, option: any) => handleChangeModalInput(value, 'year')}>

              {yearsArray.map((item: any, index: any) => (
                <Select.Option key={`year-${index}-${item}`}
                               id={item}
                               value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <MyText>
              {t('table.specialty')}
            </MyText>

            <Select style={{width: '100%'}}
                    value={currentRupState?.data?.speciality?.caption}
                    onChange={(value: any, option: any) => handleChangeModalInput(option.item, 'speciality')}
                    showSearch
                    filterOption={(input: any, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    loading={rupsHelpersState?.speciality?.loading}
            >
              {rupsHelpersState?.speciality?.data?.map((item: any, index: any) => (
                <Select.Option key={index + item.id + item.caption}
                               id={item.id}
                               value={item.id}
                               item={{
                                 id: item.id,
                                 caption: item.caption,
                                 code: item.code
                               }}>
                  {item.id + ' -' + item.code + '-' + (item.qual_types && item.qual_types !== '' ? `(квал:${item.qual_types})-` : '') + item.caption}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={24}>
            <MyText>
              {t('groups')}
            </MyText>

            <Select style={{width: '100%'}} mode="multiple"
                    value={currentRupState?.data?.linkGroups?.map((item: any) => item?.group?.id)}
                    loading={rupsHelpersState?.linkGroups?.loading}
                    onChange={(value: any, option: any) => handleChangeModalInput(option, 'linkGroups')}
                    showSearch
                    filterOption={(input: any, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {rupsHelpersState?.linkGroups?.data?.map((item: any, index: any) => (
                <Select.Option key={item.id} value={item.id} group={{
                  id: item.id,
                  caption: item.caption,
                }}>
                  {item.caption}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Modal>
    </>
  )
}
